// Utils
import { createSecureFetchUtils } from '@shopee/secure-fetch-utils';
import { getDeviceFingerprint as getDeviceFingerprintFromCookie } from '@shopee-user/device-fingerprint';
import {
  getNativeDeviceFingerprint,
  getNativeVersion,
} from '@shopee-user/business-customization-logic';
import { isWebBridgeSupported } from '@shopee-user/platform';

// Constants & Enums
import { config } from '@shopee-user/platform';
import { ABSOLUTE_URL_REGEX, CUSTOM_HEADERS, API_URL } from './constants';
import { Locale } from '@shopee-user/global';

// Types
import { WithFirebasePerformanceProps } from '@shopee-user-common/firebase-performance-utils';
import { GetConfig, PostConfig } from './types';

let getNativeDfpPromise: Promise<string> | null = null;
const isLiveEnv = config.ENV === 'live' || config.ENV === 'liveish';

async function initNativeDeviceFingerprint({
  recordFirebasePerformance,
}: {
  recordFirebasePerformance?: WithFirebasePerformanceProps['recordFirebasePerformance'];
} = {}) {
  getNativeDfpPromise = getNativeDeviceFingerprint({
    recordFirebasePerformance,
    getNativeVersion,
  });
}

async function getNativeDfp() {
  if (getNativeDfpPromise === null) {
    initNativeDeviceFingerprint();
  }
  return getNativeDfpPromise;
}

const {
  initSecuritySdk,
  get: originalGet,
  post: originalPost,
} = createSecureFetchUtils({
  locale: config.LOCALE === Locale.GLOBAL ? Locale.SG : config.LOCALE,
  // Anti Fraud side need test with non-live SDK so we will still use test SDK on ToB non-live envs
  isLiveEnv,
  // To determine whether to use new or stable sdk based on SDK grayscale config
  // Integrators should make sure this value is uniformly distributed and unique for each user
  grayUserId: getDeviceFingerprintFromCookie().toString(),
  headers: CUSTOM_HEADERS,
  // Disable short DFP on toB due to the CORS issue with the new header key
  allowShortDfp: false,
});

async function get<T>(url: string, config?: GetConfig) {
  // Support passing absolute URL
  const domain = ABSOLUTE_URL_REGEX.test(url) ? '' : API_URL;

  const disableNativeDeviceFingerPrint = !!config?.disableNativeDeviceFingerPrint;

  let finalConfig: GetConfig | undefined = config;
  let isUsingWebDfp = !!config?.antiFraudConfig?.deviceFingerPrint;

  if (!disableNativeDeviceFingerPrint && isWebBridgeSupported()) {
    const { deviceFingerPrint } = config?.antiFraudConfig || {};

    let configWithCustomDfp: GetConfig = {};

    // only send antiFraudConfig if deviceFingerprint is defined, otherwise the security sdk may init
    if (deviceFingerPrint) {
      isUsingWebDfp = false;
      configWithCustomDfp = {
        ...config,
        antiFraudConfig: {
          deviceFingerPrint: {
            ...deviceFingerPrint,
            customDfp: (await getNativeDfp()) || '',
          },
        },
      };
    }

    finalConfig = deviceFingerPrint ? configWithCustomDfp : config;
  }

  if (isUsingWebDfp && finalConfig?.antiFraudConfig?.deviceFingerPrint) {
    // By default, we will wait for DFP init to complete before sending the request
    // If DPF is not inited, FU will use old version of DFP, it means DFP is still available but not the latest version
    // Security side still can accept the old version of DFP, but ideally we should use the latest one
    const shouldWaitForDfpInitFromCaller =
      config?.antiFraudConfig?.deviceFingerPrint?.shouldWaitForDfpInit;
    finalConfig.antiFraudConfig.deviceFingerPrint.shouldWaitForDfpInit =
      shouldWaitForDfpInitFromCaller === null ||
      shouldWaitForDfpInitFromCaller === undefined
        ? true
        : shouldWaitForDfpInitFromCaller;
  }

  return originalGet<T>(`${domain}${url}`, finalConfig);
}

async function post<T>(
  url: string,
  data: Record<string, unknown>,
  config?: PostConfig
) {
  // Support passing absolute URL
  const domain = ABSOLUTE_URL_REGEX.test(url) ? '' : API_URL;

  const disableNativeDeviceFingerPrint = !!config?.disableNativeDeviceFingerPrint;

  let finalConfig: PostConfig | undefined = config;
  let isUsingWebDfp = !!config?.antiFraudConfig?.deviceFingerPrint;

  if (!disableNativeDeviceFingerPrint && isWebBridgeSupported()) {
    const { deviceFingerPrint } = config?.antiFraudConfig || {};

    let configWithCustomDfp: PostConfig = {};

    // only send antiFraudConfig if deviceFingerprint is defined, otherwise the security sdk may init
    if (deviceFingerPrint) {
      isUsingWebDfp = false;
      configWithCustomDfp = {
        ...config,
        antiFraudConfig: {
          deviceFingerPrint: {
            ...deviceFingerPrint,
            customDfp: (await getNativeDfp()) || '',
          },
        },
      };
    }

    finalConfig = deviceFingerPrint ? configWithCustomDfp : config;
  }

  if (isUsingWebDfp && finalConfig?.antiFraudConfig?.deviceFingerPrint) {
    // By default, we will wait for DFP init to complete before sending the request
    // If DPF is not inited, FU will use old version of DFP, it means DFP is still available but not the latest version
    // Security side still can accept the old version of DFP, but ideally we should use the latest one
    const shouldWaitForDfpInitFromCaller =
      config?.antiFraudConfig?.deviceFingerPrint?.shouldWaitForDfpInit;
    finalConfig.antiFraudConfig.deviceFingerPrint.shouldWaitForDfpInit =
      shouldWaitForDfpInitFromCaller === null ||
      shouldWaitForDfpInitFromCaller === undefined
        ? true
        : shouldWaitForDfpInitFromCaller;
  }

  return originalPost<T>(`${domain}${url}`, data, finalConfig);
}

export { initNativeDeviceFingerprint, initSecuritySdk, get, post };
export { API_URL, CUSTOM_HEADERS };

export type { ApiResponse as ApiResponseV2 } from '@shopee/secure-fetch-utils';
